import { createApi } from '@reduxjs/toolkit/query/react';

import { setProfile } from '../../features/auth/auth.slice';
import { IUser, IUserProfile, LoginInput } from '../../features/auth/auth.types';
import axiosBaseQuery, { getToken } from '../root';

const baseUrl = `${import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : '/api'}`;

const baseQuery = axiosBaseQuery({
  baseUrl,
});

export const AuthRTKService = createApi({
  reducerPath: 'AuthRTKService',
  baseQuery,
  tagTypes: ['EmailActive', 'PlanActive', 'Profile', 'Preferences', 'CheckStatus'],
  endpoints: builder => ({
    login: builder.mutation<any, LoginInput>({
      query: loginData => ({
        url: `/auth/login`,
        method: 'POST',
        data: loginData,
      }),
      transformResponse: response => response.element,
    }),
    deleteUser: builder.mutation<void, void>({
      query: () => ({
        url: `/auth/user`,
        method: 'DELETE',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: response => response.element,
    }),
    socialAuthLogin: builder.mutation<any, any>({
      query: loginData => ({
        url: `/auth/social/login`,
        method: 'POST',
        data: loginData,
      }),
      transformResponse: response => response.element,
    }),
    getProfile: builder.query<IUser, void>({
      query: () => ({
        url: `/profile`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['Profile'],
      transformResponse: response => response.element,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setProfile(data));
        } catch (error) {
          // Handle error silently
        }
      },
    }),
    updateProfile: builder.mutation<any, IUserProfile>({
      query: data => ({
        url: '/profile',
        method: 'PATCH',
        headers: {
          'x-auth-token': getToken(),
        },
        data,
      }),
      invalidatesTags: ['Profile'],
    }),
    updateAvatar: builder.mutation<any, any>({
      query: data => ({
        url: '/profile/avatar',
        method: 'PATCH',
        headers: {
          'x-auth-token': getToken(),
        },
        data,
      }),
    }),
    updatePassword: builder.mutation<any, any>({
      query: data => ({
        url: '/profile/password',
        method: 'POST',
        headers: {
          'x-auth-token': getToken(),
        },
        data,
      }),
    }),
    updateEmail: builder.mutation<any, any>({
      query: data => ({
        url: '/profile/email',
        method: 'PATCH',
        headers: {
          'x-auth-token': getToken(),
        },
        data,
      }),
    }),
    checkUsername: builder.query<any, string>({
      query: username => ({
        url: `/profile/username/${username}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
    }),
    deleteProfileAvatar: builder.mutation<any, void>({
      query: () => ({
        url: '/profile/avatar',
        method: 'DELETE',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
    }),
    setError: builder.mutation<any, { error: string; message: string }>({
      query: data => ({
        url: '/auth/Error',
        method: 'POST',
        headers: {
          'x-auth-token': getToken(),
        },
        data: {
          ...data,
          applicationName: 'LouiseBotFrontend',
        },
      }),
    }),
    passLessStatus: builder.mutation<
      any,
      {
        user_identification: string;
        secret_word: string;
      }
    >({
      query: ({ user_identification, secret_word }: { user_identification: string; secret_word: string }) => ({
        url: `/auth/passless/login/status/${user_identification}`,
        method: 'POST',
        data: {
          secret_word: secret_word,
        },
      }),
      // transformResponse: (response) => response.element
    }),
    socialAuthCheckUser: builder.mutation<
      any,
      {
        type: string;
        key?: string;
        partnerInfo?: {
          firstName: string;
          lastName: string;
          birthDate: string;
          companyName: string;
          address1: string;
          address2: string;
          city: string;
          country: string;
          zipCode: string;
          phone: string;
          websiteLinks: string;
          promotionPlan: string;
          monthlyViews: string;
          ambitions: string;
          termsAndConditions: true;
          textReaded: true;
          payoutInfo: Record<string, unknown>;
        };
        adminPanel?: true;
        providerPayload?: {
          access_token: string;
          authuser: string;
          expires_in: 0;
          hd: string;
          prompt: string;
          provider: string;
          scope: string;
          token_type: string;
        };
        user_utm_data?: Record<string, unknown>;
      }
    >({
      query: data => ({
        url: `/auth/social/check-user`,
        method: 'POST',
        data: data,
      }),
      transformResponse: response => response.element,
    }),
    passLessLoginFinalize: builder.mutation<any, { user_identification: string; passless_token: string }>({
      query: ({ user_identification, passless_token }) => ({
        url: `/auth/passless/login/finalize/${user_identification}`,
        method: 'POST',
        data: {
          passless_token,
        },
      }),
      transformResponse: response => response.element,
    }),
    refreshToken: builder.mutation<any, { refresh_token: string }>({
      query: ({ refresh_token }) => ({
        url: `/auth/refresh`,
        method: 'POST',
        data: {
          refresh_token: refresh_token,
        },
      }),
      // transformResponse: (response) => response.element
    }),
  }),
});

export const {
  useLoginMutation,
  useSocialAuthLoginMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUpdateAvatarMutation,
  useUpdatePasswordMutation,
  useUpdateEmailMutation,
  useDeleteProfileAvatarMutation,
  useSetErrorMutation,
  useLazyGetProfileQuery,
  useDeleteUserMutation,
  usePassLessStatusMutation,
  usePassLessLoginFinalizeMutation,
  useSocialAuthCheckUserMutation,
  useRefreshTokenMutation,
} = AuthRTKService;
